import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '@/components/ui/select';
import { getSystemTypeLabel, getSystemCategory } from '@/lib/systemTypeConstants';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Loader2, Paperclip, AlertCircle, CheckCircle2, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const PRIORIDADES = [
  { value: 'baja', label: 'Baja' },
  { value: 'media', label: 'Media' },
  { value: 'alta', label: 'Alta' },
];

const estadoLabel: Record<string, { label: string; variant: any }> = {
  nuevo: { label: 'Recibido', variant: 'secondary' },
  en_revision: { label: 'En revisión', variant: 'outline' },
  convertido_ot: { label: 'Convertido en OT', variant: 'default' },
  descartado: { label: 'Descartado', variant: 'destructive' },
};

export default function PortalAvisos() {
  const { perfil, empresa, session } = useAuth();
  const ctx = useOutletContext<{ clienteNombre: string }>();
  const [avisos, setAvisos] = useState<any[]>([]);
  const [portalUser, setPortalUser] = useState<any | null>(null);
  const [sedes, setSedes] = useState<any[]>([]);
  const [sistemas, setSistemas] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ titulo: '', descripcion: '', prioridad_sugerida: 'media', sede_id: '', sistema_id: '' });
  const [files, setFiles] = useState<File[]>([]);

  const sistemasFiltrados = form.sede_id
    ? sistemas.filter(s => s.sede_id === form.sede_id)
    : sistemas;

  async function load() {
    setLoading(true);
    const { data } = await (supabase as any)
      .from('avisos_cliente')
      .select('*')
      .order('created_at', { ascending: false });
    setAvisos(data ?? []);
    setLoading(false);
  }

  useEffect(() => {
    // asegurar bucket (idempotente)
    supabase.functions.invoke('ensure-avisos-bucket').catch(() => {});
    load();
  }, []);

  useEffect(() => {
    if (!perfil?.user_id) return;
    (async () => {
      const { data: pu } = await (supabase as any)
        .from('usuarios_portal_cliente')
        .select('id, cliente_id, empresa_id')
        .eq('user_id', perfil.user_id)
        .maybeSingle();
      if (!pu) return;
      setPortalUser(pu);

      const [{ data: sedesData }, { data: sistemasData }] = await Promise.all([
        (supabase as any)
          .from('cliente_sedes')
          .select('id, nombre, direccion, poblacion')
          .eq('cliente_id', pu.cliente_id)
          .eq('activa', true)
          .order('nombre'),
        (supabase as any)
          .from('sistemas')
          .select('id, tipo, descripcion, ubicacion, sede_id')
          .eq('cliente_id', pu.cliente_id)
          .order('tipo'),
      ]);
      setSedes(sedesData ?? []);
      setSistemas(sistemasData ?? []);
    })();
  }, [perfil?.user_id]);

  async function crear() {
    if (!form.titulo.trim()) return toast.error('El título es obligatorio');
    if (!perfil?.user_id) return toast.error('Sesión inválida');
    if (sedes.length > 0 && !form.sede_id) return toast.error('Selecciona la sede del aviso');
    if (sistemas.length > 0 && !form.sistema_id) return toast.error('Selecciona el sistema afectado');

    setSaving(true);
    try {
      const pu = portalUser ?? (await (supabase as any)
        .from('usuarios_portal_cliente')
        .select('id, cliente_id, empresa_id')
        .eq('user_id', perfil.user_id)
        .maybeSingle()).data;
      if (!pu) throw new Error('No se pudo identificar al cliente');

      const adjuntos: any[] = [];
      for (const f of files) {
        const safeName = f.name.replace(/[^\w.-]+/g, '_');
        const path = `${pu.empresa_id}/${pu.cliente_id}/${Date.now()}-${safeName}`;
        const { error: upErr } = await supabase.storage
          .from('avisos-cliente-adjuntos')
          .upload(path, f, { contentType: f.type });
        if (upErr) throw upErr;
        adjuntos.push({ path, name: f.name, size: f.size, mime_type: f.type });
      }

      const { data: inserted, error } = await (supabase as any).from('avisos_cliente').insert({
        empresa_id: pu.empresa_id,
        cliente_id: pu.cliente_id,
        creado_por_portal_user_id: pu.id,
        titulo: form.titulo.trim(),
        descripcion: form.descripcion.trim() || null,
        prioridad_sugerida: form.prioridad_sugerida,
        sede_id: form.sede_id || null,
        sistema_id: form.sistema_id || null,
        adjuntos,
      }).select('id').single();
      if (error) throw error;

      // Email de confirmación al cliente (no bloqueante)
      supabase.functions.invoke('send-email', {
        body: {
          to: perfil.email,
          subject: 'Aviso recibido',
          template: 'aviso-recibido',
          empresa_id: pu.empresa_id,
          data: {
            nombre: perfil.nombre,
            titulo: form.titulo.trim(),
            prioridad_sugerida: form.prioridad_sugerida,
            empresa_nombre: empresa?.nombre,
          },
        },
      }).catch(() => {});

      // Notificación a admins/gestores del backoffice (no bloqueante)
      if (inserted?.id) {
        const { data: sessionData } = await supabase.auth.getSession();
        const accessToken = sessionData.session?.access_token ?? session?.access_token;
        const { data: notifyData, error: notifyError } = await supabase.functions.invoke('notificar-nuevo-aviso', {
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
          body: { aviso_id: inserted.id },
        });
        if (notifyError || (notifyData as any)?.error) {
          console.error('Error notificando aviso a administradores:', notifyError || notifyData);
          toast.warning('Aviso enviado, pero no se pudo notificar por email al administrador');
        }
      }

      toast.success('Aviso enviado');
      setOpen(false);
      setForm({ titulo: '', descripcion: '', prioridad_sugerida: 'media', sede_id: '', sistema_id: '' });
      setFiles([]);
      load();
    } catch (e: any) {
      toast.error(e.message || 'No se pudo enviar el aviso');
    } finally {
      setSaving(false);
    }
  }

  async function verAdjunto(path: string) {
    const { data, error } = await supabase.storage
      .from('avisos-cliente-adjuntos')
      .createSignedUrl(path, 60);
    if (error || !data) return toast.error('No se pudo abrir el adjunto');
    window.open(data.signedUrl, '_blank');
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold">Mis avisos</h1>
          <p className="text-sm text-muted-foreground">Comunícanos incidencias o solicitudes. Las convertiremos en OT cuando corresponda.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button><Plus className="h-4 w-4 mr-2" /> Nuevo aviso</Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Nuevo aviso</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Título*</Label>
                <Input value={form.titulo} onChange={e => setForm({ ...form, titulo: e.target.value })} placeholder="Resumen del aviso" />
              </div>
              <div>
                <Label>Descripción</Label>
                <Textarea value={form.descripcion} onChange={e => setForm({ ...form, descripcion: e.target.value })} rows={4} placeholder="Detalla la incidencia, ubicación, urgencia..." />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label>Sede*</Label>
                  <Select
                    value={form.sede_id}
                    onValueChange={v => setForm({ ...form, sede_id: v, sistema_id: '' })}
                    disabled={sedes.length === 0}
                  >
                    <SelectTrigger><SelectValue placeholder="Selecciona sede" /></SelectTrigger>
                    <SelectContent>
                      {sedes.map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Sistema afectado*</Label>
                  <Select
                    value={form.sistema_id}
                    onValueChange={v => setForm({ ...form, sistema_id: v })}
                    disabled={sistemasFiltrados.length === 0}
                  >
                    <SelectTrigger><SelectValue placeholder="Selecciona sistema" /></SelectTrigger>
                    <SelectContent>
                      {(() => {
                        const seguridad = sistemasFiltrados.filter(s => getSystemCategory(s.tipo) === 'seguridad');
                        const pci = sistemasFiltrados.filter(s => getSystemCategory(s.tipo) === 'pci');
                        const renderItem = (s: any) => (
                          <SelectItem key={s.id} value={s.id}>
                            {[getSystemTypeLabel(s.tipo), s.descripcion, s.ubicacion].filter(Boolean).join(' · ')}
                          </SelectItem>
                        );
                        if (seguridad.length > 0 && pci.length > 0) {
                          return (
                            <>
                              <SelectGroup>
                                <SelectLabel>Seguridad</SelectLabel>
                                {seguridad.map(renderItem)}
                              </SelectGroup>
                              <SelectGroup>
                                <SelectLabel>PCI</SelectLabel>
                                {pci.map(renderItem)}
                              </SelectGroup>
                            </>
                          );
                        }
                        return sistemasFiltrados.map(renderItem);
                      })()}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Prioridad sugerida</Label>
                <Select value={form.prioridad_sugerida} onValueChange={v => setForm({ ...form, prioridad_sugerida: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRIORIDADES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">Nuestro equipo confirmará la prioridad final.</p>
              </div>
              <div>
                <Label>Adjuntos (fotos / PDF)</Label>
                <Input
                  type="file"
                  multiple
                  accept="image/*,application/pdf"
                  onChange={e => setFiles(Array.from(e.target.files ?? []))}
                />
                {files.length > 0 && (
                  <ul className="mt-2 text-xs space-y-1">
                    {files.map((f, i) => (
                      <li key={i} className="flex items-center justify-between gap-2 bg-muted rounded px-2 py-1">
                        <span className="truncate">{f.name}</span>
                        <button onClick={() => setFiles(files.filter((_, j) => j !== i))}><X className="h-3 w-3" /></button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button onClick={crear} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Enviar aviso
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : avisos.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">
          <AlertCircle className="h-8 w-8 mx-auto mb-3 opacity-50" />
          Aún no has enviado ningún aviso.
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {avisos.map(a => {
            const estado = estadoLabel[a.estado] ?? { label: a.estado, variant: 'outline' };
            const adjuntos = Array.isArray(a.adjuntos) ? a.adjuntos : [];
            return (
              <Card key={a.id}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <CardTitle className="text-base">{a.titulo}</CardTitle>
                    <Badge variant={estado.variant}>{estado.label}</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {format(new Date(a.created_at), "d MMM yyyy HH:mm", { locale: es })}
                    {' · prioridad sugerida: '}<span className="capitalize">{a.prioridad_sugerida}</span>
                    {a.prioridad && <> · prioridad confirmada: <span className="capitalize font-medium">{a.prioridad}</span></>}
                  </p>
                </CardHeader>
                {(a.descripcion || adjuntos.length > 0 || a.motivo_descarte) && (
                  <CardContent className="space-y-3 text-sm">
                    {a.descripcion && <p className="whitespace-pre-wrap">{a.descripcion}</p>}
                    {adjuntos.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {adjuntos.map((adj: any, i: number) => (
                          <Button key={i} size="sm" variant="outline" onClick={() => verAdjunto(adj.path)}>
                            <Paperclip className="h-3 w-3 mr-1" /> {adj.name || `Adjunto ${i + 1}`}
                          </Button>
                        ))}
                      </div>
                    )}
                    {a.estado === 'convertido_ot' && (
                      <div className="flex items-center gap-2 text-xs text-green-700 dark:text-green-400">
                        <CheckCircle2 className="h-4 w-4" /> Convertido en orden de trabajo
                      </div>
                    )}
                    {a.estado === 'descartado' && a.motivo_descarte && (
                      <div className="text-xs text-muted-foreground">
                        <strong>Motivo:</strong> {a.motivo_descarte}
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
